/*eslint complexity: ["error", 30]*/
import React from 'react';
import 'react-circular-progressbar/dist/styles.css';

import { Grid } from '../../../common/components/Grid/Grid';
import { GridColumn } from '../../../common/components/Grid/GridDataTypes';
import numberFormatter from '../../../common/numberFormatter';
import {
  GetDictionaryDto,
  GetPaeEstimateItemsDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';

import { PaeParameterMapperForm } from './PaeParameterMapperForm';
import { getPaePositionTeamName, isPaePositionCorrect } from './PaeUtils';

interface PaePositionsGridProps {
  data: GetPaeEstimateItemsDto[];
  countriesDictionaries?: GetDictionaryDto[];
  pathPhaseDictionaries?: GetDictionaryDto[];
  companyId?: string;
  budgetId?: string;
  enableMultiSelect?: boolean;
  purchaseOnly?: boolean;
  pathPhaseEnabled?: boolean;
  selectedKeys?: string[];
  refresh?: boolean;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  updateParams?: (
    rowId: string,
    newPurchaseFormDictId: string,
    newPurchaseFormDictValue: string,
    newPurchaseFormDictValueEn: string,
    newCountryDictId: string,
    newCountryDictValue: string,
    newCountryDictValueEn: string,
    newBallDictId: string,
    newBallDictValue: string,
    newBallDictValueEn: string,
    newActionGroup: string,
    newPathPhaseDictId: string,
    newPathPhaseDictValue: string,
    newPathPhaseDictValueEn: string,
    newSpecialistTeamId: string,
    newSpecialistTeamName: string,
  ) => void;
}

export const PaePositionsGrid: React.FC<PaePositionsGridProps> = props => {
  const { t, tk } = useTranslation('projectLng');
  const pok = usePokCore();
  const lang = pok.getLanguage();
  const columns: GridColumn[] = [
    { header: t(tk.estimates.positionName), property: 'positionName' },
    { header: '', property: 'changeButton', noSorting: true },
    { header: t(tk.estimates.positionCountry), property: 'countryDict' },
    ...(props.pathPhaseEnabled
      ? [{ header: t(tk.estimates.positionPhase), property: 'pathPhaseDict' }]
      : []),
    {
      header: t(tk.estimates.deliveryTeam),
      property: 'specialistTeamName',
    },
    { header: t(tk.estimates.ball), property: 'ballDict' },
    { header: t(tk.estimates.purchaseForm), property: 'purchaseForm' },
    { header: t(tk.estimates.actionGroup), property: 'actionGroup' },
    ...(!props.purchaseOnly
      ? [{ header: t(tk.estimates.sales), property: 'sale' }]
      : []),
    { header: t(tk.estimates.purchase), property: 'purchase' },
    { header: t(tk.estimates.paePurchaseForm), property: 'paePurchaseForm' },
    {
      header: t(tk.estimates.paePositionNumber),
      property: 'paePositionNumber',
    },
  ];

  const generateDictionaryFieldValue = (
    rowId: string,
    paePosition: GetPaeEstimateItemsDto,
  ) => {
    return (
      <PaeParameterMapperForm
        paePosition={paePosition}
        companyId={props.companyId}
        budgetId={props.budgetId}
        countriesDictionaries={props.countriesDictionaries}
        pathPhaseDictionaries={props.pathPhaseDictionaries}
        pathPhaseEnabled={props.pathPhaseEnabled}
        existingParamLabel={t(tk.estimates.paePurchaseForm)}
        handleUpdate={props.updateParams}
      />
    );
  };

  const data = props.data.map(paePosition => {
    const incomplete = !isPaePositionCorrect(paePosition);
    return {
      key: paePosition.idx,
      values: {
        positionName: paePosition.position?.name
          ? `${paePosition.position?.name} (${paePosition.position?.client.name})`
          : '-',
        paePositionNumber: paePosition.paePositionSystemNumber,
        changeButton: paePosition.position?.name
          ? generateDictionaryFieldValue(paePosition.idx, paePosition)
          : '',
        purchaseForm:
          (lang === 'PL'
            ? paePosition.purchaseFormDict?.value
            : paePosition.purchaseFormDict?.valueEn) || '-',
        paePurchaseForm: paePosition.paePurchaseForm,
        purchase: numberFormatter(paePosition.purchaseAmount, true),
        sale: numberFormatter(paePosition.salesAmount, true),
        countryDict:
          (lang === 'PL'
            ? paePosition?.countryDict?.value
            : paePosition?.countryDict?.valueEn) || '',
        pathPhaseDict:
          (lang === 'PL'
            ? paePosition?.pathPhaseDict?.value
            : paePosition?.pathPhaseDict?.valueEn) || '',
        specialistTeamName: getPaePositionTeamName(paePosition),
        ballDict:
          (lang === 'PL'
            ? paePosition?.ballDict?.value
            : paePosition?.ballDict?.valueEn) || '',
        actionGroup: paePosition.actionGroup,
      },
      tdCssClass: {
        purchase: 'amounts-align-right',
        sale: 'amounts-align-right',
      },
      readOnly: incomplete,
      isInactive: incomplete,
    };
  });

  return (
    <Grid
      data={data}
      columns={columns}
      showFilter={true}
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.estimates.noPositions)}
      trClassName="font-90"
      enableMultiSelect={props.enableMultiSelect}
      onRowClick={props.onRowClick}
      selectedKeys={props.selectedKeys}
      refresh={props.refresh}
    />
  );
};
